import React, {useEffect, useCallback, useMemo, useRef} from 'react'
import {useSelector, useDispatch} from 'react-redux'
import {Select, Button} from '@mdsol/onex-design'
import {DataGrid} from '@mdsol/onex-design/DataGrid'
import _isEqual from 'lodash.isequal'
import {DiagnosticType, SortType} from 'api/graphQL/generated/graphql'
import DiagnosticDetailsPanel from './DiagnosticDetailsPanel'
import GridControl from './GridControl'
import DiagnosticIdCell from './cell/DiagnosticIdCell'
import {AppDispatch} from 'redux/config'
import {Status} from 'commonTypes'
import './index.scss'
import 'components/common/grid/grid.scss'
import {URLOption} from 'redux/diagnostics/state'

import {
  fetchDiagnosticsUrls,
  fetchDiagnosticsScripts,
  RunDiagnostic,
  diagnosticsAC,
} from 'redux/diagnostics/diagnostics.duck'

import {
  select_urlOptions,
  select_diagnosticScriptOptions,
  select_status,
  select_diagnosticResults,
  select_columnsconfig,
  select_totalCount,
  select_selectedUrl,
  select_selectedScript,
  select_gridSession_pagination,
  select_gridSession_sorting,
  select_shouldRunDiagnostic,
  select_gridSession_columnOrder,
  select_diagnosticDetailsItem,
  select_showDetailsPanel,
  select_gridSession_filters_searchQuery,
} from 'redux/diagnostics/selectors'

const Diagnostics = () => {
  const dispatch = useDispatch<AppDispatch>()

  const shouldRunDiagnostics = useSelector(select_shouldRunDiagnostic)

  const urlOptions = useSelector(select_urlOptions)
  const diagnosticScriptOptions = useSelector(select_diagnosticScriptOptions)
  const status = useSelector(select_status)
  const diagnosticResults = useSelector(select_diagnosticResults)
  const columnsConfig = useSelector(select_columnsconfig)
  const totalCount = useSelector(select_totalCount)

  const selectedUrl = useSelector(select_selectedUrl)
  const selectedScript = useSelector(select_selectedScript)
  const pagination = useSelector(select_gridSession_pagination)
  const sorting = useSelector(select_gridSession_sorting)
  const diagnosticDetailsItem = useSelector(select_diagnosticDetailsItem)
  const showDetailsPanel = useSelector(select_showDetailsPanel)
  const columnOrder = useSelector(select_gridSession_columnOrder)
  const justResetSortingRef = useRef(false)
  const selectedUrlOption = urlOptions.find(opt => opt.value === selectedUrl) ?? null
  const selectedScriptOption = diagnosticScriptOptions.find(opt => opt.value === selectedScript) ?? null
  const searchQuery = useSelector(select_gridSession_filters_searchQuery)
  const gridState = useMemo(() => ({
    sorting,
    columnOrder,
  }), [sorting, columnOrder])

  const columnsWithClickableId = useMemo(() => {
    if (!columnsConfig.length) return []

    return columnsConfig.map((col, index) => {
      if (index !== 0) return col

      return {
        className: 'diagnostic-id-cell',
        ...col,
        cell: ({row}: any) => (
          <DiagnosticIdCell
            row={row}
            col={col}
            selectedItem={diagnosticDetailsItem}
            diagnosticResults={diagnosticResults}
          />
        ),
      }
    })
  }, [columnsConfig, diagnosticResults, diagnosticDetailsItem])

  useEffect(() => {
    dispatch(fetchDiagnosticsUrls())
    dispatch(fetchDiagnosticsScripts())
  }, [dispatch])

  useEffect(() => {
    if (!shouldRunDiagnostics || !selectedUrl || !selectedScript) return

    const sortState = sorting?.[0]
    const effectiveSort = sortState
    const sortingInput = effectiveSort?.id
      ? {column: sortState.id, direction: sortState.desc ? SortType.Desc : SortType.Asc}
      : undefined

    dispatch(
      RunDiagnostic(
        selectedUrl,
        selectedScript,
        pagination.pageIndex * pagination.pageSize,
        pagination.pageSize,
        searchQuery,
        sortingInput
      )
    )

    dispatch(diagnosticsAC.setShouldRunDiagnostic(false))
    dispatch(diagnosticsAC.clearDiagnosticDetailsItem())
  }, [
    shouldRunDiagnostics,
    selectedUrl,
    selectedScript,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    searchQuery,
    columnsConfig,
    dispatch,
  ])

  const handleClosePanel = () => {
    dispatch(diagnosticsAC.clearDiagnosticDetailsItem())
  }

  const handleRunDiagnostic = () => {
    if (!selectedUrl || !selectedScript) return
    justResetSortingRef.current = true 
    dispatch(diagnosticsAC.updatePagination({pageIndex: 0, pageSize: pagination.pageSize}))
    dispatch(diagnosticsAC.updateSorting([]))
    dispatch(diagnosticsAC.setShouldRunDiagnostic(true))
  }

  const handleSortingChange = useCallback((tableState: any) => {
    if (justResetSortingRef.current) {
      justResetSortingRef.current = false
      return
    }
    const sortingState = tableState.sorting
    if (!Array.isArray(sortingState)) return
    if (!_isEqual(sorting, sortingState)) {
      dispatch(diagnosticsAC.updateSorting(sortingState))
      dispatch(diagnosticsAC.setShouldRunDiagnostic(true))
    }
  }, [dispatch, sorting])

  const handleColumnOrderChange = useCallback((newOrder: string[]) => {
    dispatch(diagnosticsAC.updateColumnOrder(newOrder))
  }, [dispatch])

  const handlePaginationChange = useCallback((next: any) => {
    const nextPagination = typeof next === 'function' ? next(pagination) : next

    const newPagination = {
      pageIndex: Number.isInteger(nextPagination.pageIndex) ? nextPagination.pageIndex : 0,
      pageSize: Number.isInteger(nextPagination.pageSize) ? nextPagination.pageSize : 10,
    }

    if (
      newPagination.pageIndex === pagination.pageIndex &&
      newPagination.pageSize === pagination.pageSize
    ) {
      return
    }

    dispatch(diagnosticsAC.updatePagination(newPagination))
    dispatch(diagnosticsAC.setShouldRunDiagnostic(true))
  }, [dispatch, pagination])

  const isLoading = status === Status.PENDING

  return (
    <div id='diagnostic-tool'>
      <div className='diagnostic-row'>
        <Select
          key='url-selector'
          className='diagnostic-ddl'
          dataTestId='select-url'
          options={urlOptions}
          value={selectedUrlOption}
          onSelect={(value: URLOption) =>
            dispatch(diagnosticsAC.setSelectedUrl(value?.value ?? null))
          }
          label='URL'
          size='sm'
          required
        />
        <Select
          key='script-selector'
          className='diagnostic-ddl'
          dataTestId='select-diagnostic-script'
          options={diagnosticScriptOptions}
          value={selectedScriptOption}
          onSelect={(value: { label: string; value: DiagnosticType }) =>
            dispatch(diagnosticsAC.setSelectedScript(value?.value ?? null))
          }
          label='Diagnostic Script'
          size='sm'
          required
        />
        <div className='button-container'>
          <Button
            key='run-diagnostic-button'
            dataTestId='run-diagnostic-button'
            onClick={handleRunDiagnostic}
            size='sm'
            disabled={!(selectedUrlOption && selectedScriptOption)}
          >
            Run Diagnostic
          </Button>
        </div>
      </div>

      <div id='grid-container' className={showDetailsPanel ? 'with-row-details' : ''}>
        <div className='main-content'>
          <DataGrid
            dataTestId='diagnostic-results-grid'
            customGridControl={<GridControl key='key-databases-grid-control' />}
            columns={columnsWithClickableId}
            data={diagnosticResults.map((item) => item.data || {})}
            state={gridState}
            isLoading={isLoading}
            manualSorting
            enableSorting
            onChangeControl={handleSortingChange}
            onColumnOrderChange={handleColumnOrderChange}
            enablePagination
            pagination={pagination}
            rowsPerPageOptions={[10, 25, 50]}
            pageCount={Math.ceil(totalCount / pagination.pageSize)}
            manualDataLength={totalCount}
            onPaginationChange={handlePaginationChange}
            emptyStateOptions={{
              title: 'No Results',
              subtitle: 'Run a diagnostic to see results.',
              defaultImgVariant: 'noData',
            }}
          />
        </div>

        {showDetailsPanel && diagnosticDetailsItem && (
          <div className='details-panel'>
            <DiagnosticDetailsPanel item={diagnosticDetailsItem} onClose={handleClosePanel} />
          </div>
        )}
      </div>
    </div>
  )
}

export default Diagnostics
